import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { InProductHelpLink } from '@atlassian/jira-servicedesk-common/src/ui/components/in-product-help-link';
import { sectionParagraphStyles, fieldDescriptionStyles } from '../index';
import messages from './messages';

type Props = {
	isFieldMissing?: boolean;
	info?: ReactNode;
	icon: ReactNode;
	title: string;
	afterTitleContent?: ReactNode;
	description: string;
	link: {
		href: string;
		text: string;
		inProductHelpArticleId?: string;
	};
};

export const NewField = ({
	icon,
	title,
	afterTitleContent,
	description,
	isFieldMissing = false,
	info,
	link: { href, text, inProductHelpArticleId },
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onLearnToShowLinkClick = (articleId: undefined | string) => {
		fireUIAnalytics(
			createAnalyticsEvent({}),
			'helpLink clicked',
			'newIncidentManagementDescriptionLearnToShowFieldsLink',
			{ articleId },
		);
	};

	return (
		<SubtitledSection>
			<SectionSubheadingContainer>
				{icon}
				<Box xcss={sectionSubheadingTextContainerStyles}>
					{ff('im_feature_enablement_gating') ? (
						<Flex alignItems="start">
							<SectionIconSubheading>{title}</SectionIconSubheading>
							{afterTitleContent}
						</Flex>
					) : (
						<SectionIconSubheading>{title}</SectionIconSubheading>
					)}
					{isFieldMissing && (
						<SubheadingInlineWarning>
							<WarningIcon
								size="medium"
								label=""
								primaryColor={token('color.icon.warning', colors.Y200)}
							/>
							<SubheadingInlineWarningMessage>
								{formatMessage(messages.missingFieldWarning)}
							</SubheadingInlineWarningMessage>
						</SubheadingInlineWarning>
					)}
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
					<Box xcss={[sectionParagraphStyles, fieldDescriptionStyles]}>{description}</Box>
					<InProductHelpLink
						href={href}
						learnMoreText={text}
						inProductHelpArticleId={inProductHelpArticleId}
						onClick={() => onLearnToShowLinkClick(inProductHelpArticleId)}
					/>
					{info}
				</Box>
			</SectionSubheadingContainer>
		</SubtitledSection>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SubtitledSection = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N500),
	marginTop: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionSubheadingContainer = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionIconSubheading = styled.h5({
	paddingTop: token('space.050', '4px'),
	marginBottom: token('space.200', '16px'),
});

const sectionSubheadingTextContainerStyles = xcss({
	marginLeft: 'space.150',
	marginBottom: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SubheadingInlineWarning = styled.span({
	display: 'flex',
	marginLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SubheadingInlineWarningMessage = styled.span({
	lineHeight: token('space.200', '16px'),
	paddingTop: token('space.050', '4px'),
	marginLeft: token('space.050', '4px'),
	fontWeight: 500,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N200),
});
